import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Trash2, Save } from 'lucide-react';
import { pollsAPI, userAPI, usersAPI, teamAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface Question {
  question_text: string;
  question_type: 'text' | 'textarea' | 'radio' | 'checkbox' | 'dropdown' | 'rating' | 'date';
  is_required: boolean;
  options: string[];
}

interface Recipient {
  recipient_type: 'team' | 'user' | 'all';
  recipient_id: number | null;
}

interface CreatePollModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const CreatePollModal: React.FC<CreatePollModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [deadline, setDeadline] = useState('');
  const [allowShareResults, setAllowShareResults] = useState(false);
  const [allowResponsesAfterDeadline, setAllowResponsesAfterDeadline] = useState(false);
  const [questions, setQuestions] = useState<Question[]>([
    { question_text: '', question_type: 'text', is_required: false, options: [] }
  ]);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = 'create_poll_draft_v1';

  useEffect(() => {
    if (isOpen) {
      // Restore draft
      try {
        const saved = localStorage.getItem(draftKey);
        if (saved) {
          const parsed = JSON.parse(saved);
          setTitle(parsed.title || '');
          setDescription(parsed.description || '');
          setIsAnonymous(parsed.isAnonymous || false);
          setDeadline(parsed.deadline || '');
          setAllowShareResults(parsed.allowShareResults || false);
          setAllowResponsesAfterDeadline(parsed.allowResponsesAfterDeadline || false);
          setQuestions(Array.isArray(parsed.questions) && parsed.questions.length > 0 
            ? parsed.questions 
            : [{ question_text: '', question_type: 'text', is_required: false, options: [] }]);
          setRecipients(Array.isArray(parsed.recipients) ? parsed.recipients : []);
        } else {
          // Reset form if no draft
          setTitle('');
          setDescription('');
          setIsAnonymous(false);
          setDeadline('');
          setAllowShareResults(false);
          setAllowResponsesAfterDeadline(false);
          setQuestions([{ question_text: '', question_type: 'text', is_required: false, options: [] }]);
          setRecipients([]);
        }
      } catch (error) {
        console.error('Error loading draft:', error);
        // Reset form on error
        setTitle('');
        setDescription('');
        setIsAnonymous(false);
        setDeadline('');
        setAllowShareResults(false);
        setAllowResponsesAfterDeadline(false);
        setQuestions([{ question_text: '', question_type: 'text', is_required: false, options: [] }]);
        setRecipients([]);
      }
      
      setIsSubmitting(false);
      
      // Fetch users and teams for recipient selection
      Promise.all([
        usersAPI.getAllUsers(),
        teamAPI.getTeams()
      ]).then(([usersData, teamsData]) => {
        setUsers(usersData);
        setTeams(teamsData);
      }).catch((error) => {
        console.error('Error fetching users/teams:', error);
        toast.error('Failed to load users and teams');
      });
    }
  }, [isOpen]);

  // Function to save draft
  const saveDraft = useCallback(() => {
    try {
      // Only save if there's actual content
      const hasContent = title.trim() || 
                        description.trim() || 
                        deadline.trim() ||
                        questions.some(q => q.question_text.trim() || q.options.some(opt => opt.trim())) ||
                        recipients.length > 0;
      
      if (hasContent) {
        const draftData = {
          title,
          description,
          isAnonymous,
          deadline,
          allowShareResults,
          allowResponsesAfterDeadline,
          questions,
          recipients
        };
        localStorage.setItem(draftKey, JSON.stringify(draftData));
        setDraftSaved(true);
        // Hide the "draft saved" indicator after 2 seconds
        setTimeout(() => setDraftSaved(false), 2000);
      }
    } catch (error) {
      console.error('Error saving draft:', error);
    }
  }, [title, description, isAnonymous, deadline, allowShareResults, allowResponsesAfterDeadline, questions, recipients, draftKey]);

  // Autosave draft when form data changes (with debouncing)
  useEffect(() => {
    if (!isOpen) return;
    const timeoutId = setTimeout(saveDraft, 500); // Debounce for 500ms
    return () => clearTimeout(timeoutId);
  }, [title, description, isAnonymous, deadline, allowShareResults, allowResponsesAfterDeadline, questions, recipients, isOpen, saveDraft]);

  // Save draft when window loses focus
  useEffect(() => {
    const handleBeforeUnload = () => {
      saveDraft();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        saveDraft();
      }
    };

    if (isOpen) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      document.addEventListener('visibilitychange', handleVisibilityChange);
    }

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [isOpen, saveDraft]);

  const addQuestion = () => {
    setQuestions([...questions, { question_text: '', question_type: 'text', is_required: false, options: [] }]);
  };

  const removeQuestion = (index: number) => {
    setQuestions(questions.filter((_, i) => i !== index));
  };

  const updateQuestion = (index: number, field: keyof Question, value: any) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };
    setQuestions(updated);
  };

  const addOption = (questionIndex: number) => {
    const updated = [...questions];
    updated[questionIndex].options = [...updated[questionIndex].options, ''];
    setQuestions(updated);
  };

  const updateOption = (questionIndex: number, optionIndex: number, value: string) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);
  };

  const removeOption = (questionIndex: number, optionIndex: number) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter((_, i) => i !== optionIndex);
    setQuestions(updated);
  };

  const addRecipient = (type: 'team' | 'user' | 'all', id: number | null = null) => {
    // If adding 'all', remove other recipients
    if (type === 'all') {
      setRecipients([{ recipient_type: 'all', recipient_id: null }]);
    } else {
      // Check if already exists
      const exists = recipients.some(r => r.recipient_type === type && r.recipient_id === id);
      if (!exists) {
        // Remove 'all' if it exists
        const filtered = recipients.filter(r => r.recipient_type !== 'all');
        setRecipients([...filtered, { recipient_type: type, recipient_id: id }]);
      }
    }
  };

  const removeRecipient = (type: 'team' | 'user' | 'all', id: number | null = null) => {
    setRecipients(recipients.filter(r => !(r.recipient_type === type && r.recipient_id === id)));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      toast.error('Please enter a poll title');
      return;
    }

    if (questions.some(q => !q.question_text.trim())) {
      toast.error('Please fill in all questions');
      return;
    }

    if (recipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }

    // Validate options for radio/checkbox/dropdown
    for (const question of questions) {
      if (['radio', 'checkbox', 'dropdown'].includes(question.question_type)) {
        if (question.options.length < 2 || question.options.some(opt => !opt.trim())) {
          toast.error('Please provide at least 2 options for multiple choice questions');
          return;
        }
      }
    }

    setIsSubmitting(true);
    try {
      await pollsAPI.createPoll({
        title: title.trim(),
        description: description.trim() || null,
        is_anonymous: isAnonymous,
        deadline: deadline || null,
        allow_responses_after_deadline: allowResponsesAfterDeadline,
        allow_share_results: allowShareResults,
        status: 'active',
        questions: questions.map((q, index) => ({
          question_text: q.question_text.trim(),
          question_type: q.question_type,
          is_required: q.is_required,
          options: q.options.length > 0 ? q.options.filter(opt => opt.trim()) : null,
          display_order: index
        })),
        recipients: recipients
      });
      
      toast.success('Poll created successfully!');
      // Clear draft on success
      try {
        localStorage.removeItem(draftKey);
      } catch {}
      onClose();
      onSuccess();
      // Reset form
      setTitle('');
      setDescription('');
      setIsAnonymous(false);
      setDeadline('');
      setAllowShareResults(false);
      setAllowResponsesAfterDeadline(false);
      setQuestions([{ question_text: '', question_type: 'text', is_required: false, options: [] }]);
      setRecipients([]);
    } catch (error: any) {
      console.error('Error creating poll:', error);
      toast.error(error.response?.data?.error || 'Failed to create poll');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/50"
      onClick={(e) => {
        // Close modal when clicking backdrop
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div 
        className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-gray-900">Create New Poll</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Auto-save notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <div className="flex items-center space-x-2 text-blue-700 text-sm">
              <Save className="h-4 w-4" />
              <span>
                {draftSaved ? 'Draft saved!' : 'Your work is automatically saved as you type. You can safely navigate away and return later.'}
              </span>
            </div>
          </div>

          {/* Basic Info */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Poll Title <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                placeholder="Enter poll title"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
                rows={3}
                placeholder="Enter poll description (optional)"
              />
            </div>
          </div>

          {/* Settings */}
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="responseType"
                  checked={isAnonymous}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setIsAnonymous(true);
                      setAllowShareResults(false);
                    }
                  }}
                  className="w-4 h-4 text-[var(--accent)] border-gray-300 focus:ring-[var(--accent)]"
                />
                <span className="text-sm text-gray-700">Anonymous responses</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="responseType"
                  checked={allowShareResults}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setAllowShareResults(true);
                      setIsAnonymous(false);
                    }
                  }}
                  className="w-4 h-4 text-[var(--accent)] border-gray-300 focus:ring-[var(--accent)]"
                />
                <span className="text-sm text-gray-700">Response with email</span>
              </label>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Deadline (optional)
              </label>
              <input
                type="datetime-local"
                value={deadline}
                onChange={(e) => setDeadline(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)] focus:border-[var(--accent)]"
              />
              {deadline && deadline.trim() !== '' && (
                <div className="mt-3 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={allowResponsesAfterDeadline}
                      onChange={(e) => setAllowResponsesAfterDeadline(e.target.checked)}
                      className="w-4 h-4 text-[var(--accent)] border-gray-300 rounded focus:ring-[var(--accent)]"
                    />
                    <span className="text-sm text-gray-700 font-medium">Allow responses after deadline</span>
                  </label>
                  <p className="text-xs text-gray-500 mt-1 ml-6">
                    If checked, users can still submit responses even after the deadline has passed.
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Recipients */}
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Recipients <span className="text-red-500">*</span>
            </label>
            <div className="space-y-2">
              <button
                type="button"
                onClick={() => addRecipient('all')}
                className="px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                Select All
              </button>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Teams</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {teams.map(team => (
                      <label key={team.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={recipients.some(r => r.recipient_type === 'team' && r.recipient_id === team.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addRecipient('team', team.id);
                            } else {
                              removeRecipient('team', team.id);
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span>{team.full_name || team.team_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Users</label>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {users.map(user => (
                      <label key={user.id} className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={recipients.some(r => r.recipient_type === 'user' && r.recipient_id === user.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              addRecipient('user', user.id);
                            } else {
                              removeRecipient('user', user.id);
                            }
                          }}
                          className="w-4 h-4"
                        />
                        <span>{user.first_name} {user.last_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <label className="block text-sm font-medium text-gray-700">
                Questions <span className="text-red-500">*</span>
              </label>
              <button
                type="button"
                onClick={addQuestion}
                className="flex items-center gap-2 px-3 py-1 text-sm bg-[var(--accent)] text-white rounded-lg hover:opacity-90"
              >
                <Plus className="w-4 h-4" />
                Add Question
              </button>
            </div>
            {questions.map((question, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4 space-y-3">
                <div className="flex items-start justify-between">
                  <span className="text-sm font-medium text-gray-700">Question {index + 1}</span>
                  {questions.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeQuestion(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <input
                  type="text"
                  value={question.question_text}
                  onChange={(e) => updateQuestion(index, 'question_text', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[var(--accent)]"
                  placeholder="Enter question text"
                  required
                />
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-600 mb-1">Type</label>
                    <select
                      value={question.question_type}
                      onChange={(e) => updateQuestion(index, 'question_type', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="text">Text</option>
                      <option value="textarea">Textarea</option>
                      <option value="radio">Radio</option>
                      <option value="checkbox">Checkbox</option>
                      <option value="dropdown">Dropdown</option>
                      <option value="rating">Rating</option>
                      <option value="date">Date</option>
                    </select>
                  </div>
                  <div className="flex items-center pt-6">
                    <label className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={question.is_required}
                        onChange={(e) => updateQuestion(index, 'is_required', e.target.checked)}
                        className="w-4 h-4"
                      />
                      <span className="text-sm text-gray-700">Required</span>
                    </label>
                  </div>
                </div>
                {['radio', 'checkbox', 'dropdown'].includes(question.question_type) && (
                  <div className="space-y-2">
                    <label className="block text-xs text-gray-600">Options</label>
                    {question.options.map((option, optIndex) => (
                      <div key={optIndex} className="flex items-center gap-2">
                        <input
                          type="text"
                          value={option}
                          onChange={(e) => updateOption(index, optIndex, e.target.value)}
                          className="flex-1 px-3 py-1 border border-gray-300 rounded text-sm"
                          placeholder={`Option ${optIndex + 1}`}
                        />
                        <button
                          type="button"
                          onClick={() => removeOption(index, optIndex)}
                          className="text-red-500"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <button
                      type="button"
                      onClick={() => addOption(index)}
                      className="text-sm text-[var(--accent)] hover:underline"
                    >
                      + Add Option
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-gray-700 border border-gray-300 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 rounded-lg bg-[var(--accent)] text-white font-medium hover:opacity-90 disabled:opacity-50"
            >
              {isSubmitting ? 'Creating...' : 'Create Poll'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreatePollModal;
